/**
 * useBilderGallery — Daten + Mutationen des Bilder-Tabs an einem Ort.
 *
 * Kapselt Property-/Foto-Queries + Upload/Cover/Delete-Mutationen, damit
 * BilderTab reines Layout bleibt. Fotos sind lokaler Viewer-Content
 * (FotoMarketing) — sie gehen NICHT zu OTAs.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { useUploadQueue } from '@/shared/hooks/useUploadQueue';
import { downscalePortalPhoto } from '@/shared/lib/downscaleImage';
import { toastUploadOutcome } from '@/shared/lib/uploadSummary';
import { propertyKeys } from '../queries';

export const MAX_IMAGES = 30;
export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

async function toastApiError(err: unknown) {
  const e = await toApiError(err);
  toast.error(e.detail ?? e.title ?? (err as Error).message);
}

export function useBilderGallery(id: string) {
  const api = useApi();
  const qc = useQueryClient();

  const { data: property } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const { data: images = [], isLoading } = useQuery({
    queryKey: ['propertyFiles', 'images', id],
    queryFn: () => api.propertyFiles.list(id, { kind: 'FotoMarketing' }),
  });

  const coverFileId = property?.coverFileId ?? null;
  const cover = coverFileId ? (images.find((img) => img.id === coverFileId) ?? null) : null;

  const invalidateImages = () =>
    qc.invalidateQueries({ queryKey: ['propertyFiles', 'images', id] });
  const invalidateDetail = () => qc.invalidateQueries({ queryKey: propertyKeys.detail(id) });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // Erst verkleinern, dann prüfen: ein 14-MB-Handyfoto ist nach dem Verkleinern auf 2560 px
      // nur noch ein Bruchteil — und Gäste blättern dann nicht mehr durch Originale (wie Akte).
      const prepared = await downscalePortalPhoto(file);
      if (prepared.size > MAX_SIZE_BYTES) {
        const megabytes = Math.round(prepared.size / 1024 / 1024);
        // Bleibt das Original über der Grenze, hat das Verkleinern nicht geklappt — das sagt die
        // Meldung, sonst sucht der Nutzer den Fehler an der falschen Stelle.
        throw new Error(
          prepared === file
            ? `Datei zu gross (${megabytes} MB) und im Browser nicht verkleinerbar — max 10 MB.`
            : `Datei zu gross (${megabytes} MB) — max 10 MB.`,
        );
      }
      return api.propertyFiles.upload(id, prepared, { kind: 'FotoMarketing' });
    },
    // Titelbild und Erfolgsmeldung setzt der Stapel — einmal, nicht je Bild.
    onSuccess: () => invalidateImages(),
    onError: toastApiError,
  });
  const queue = useUploadQueue();

  const setCover = useMutation({
    mutationFn: (fileId: string | null) => api.properties.setCover(id, fileId),
    onSuccess: async () => {
      await invalidateDetail();
      await qc.invalidateQueries({ queryKey: propertyKeys.lists() });
      toast.success('Cover aktualisiert');
    },
    onError: toastApiError,
  });

  const removeImage = useMutation({
    mutationFn: (fileId: string) => api.propertyFiles.delete(id, fileId),
    onSuccess: async (_, fileId) => {
      await invalidateImages();
      // War das geloeschte Bild Cover, Cover entfernen.
      if (coverFileId === fileId) {
        await api.properties.setCover(id, null);
        await invalidateDetail();
      }
      toast.success('Bild gelöscht');
    },
    onError: toastApiError,
  });

  const handleFiles = (files: FileList | File[]) => {
    // Ohne Titelbild wird das erste angekommene Bild dazu — genau eines, auch im Stapel. Die
    // Frage stand vorher in jedem einzelnen Upload und sah dort noch den alten Stand.
    let needsCover = !coverFileId;
    void queue
      .run(files, MAX_IMAGES - images.length, async (file) => {
        const created = await upload.mutateAsync(file);
        if (!needsCover) return;
        needsCover = false;
        await api.properties.setCover(id, created.id).catch(toastApiError);
        await invalidateDetail();
      })
      .then((outcome) =>
        toastUploadOutcome(outcome, `Limit erreicht: max ${MAX_IMAGES} Bilder pro Apartment`),
      );
  };

  return {
    property,
    images,
    isLoading,
    cover,
    coverFileId,
    setCover,
    removeImage,
    handleFiles,
    uploadProgress: queue.progress,
  };
}
